import React, { useCallback, useState } from 'react'
import { useSettings } from '../context/SettingsContext'
import { CameraDevice } from '../hooks/useCamera'

function gcd (a: number, b: number): number {
  return b === 0 ? a : gcd(b, a % b)
}

const resolutions = [
  // 4:3
  { width: 640, height: 480 },
  { width: 800, height: 600 },
  { width: 1024, height: 768 },
  // { width: 1600, height: 1200 },

  // 16:9
  { width: 1280, height: 720 },
  { width: 1920, height: 1080 },
  // { width: 2560, height: 1440 },

  // 16:10
  { width: 1280, height: 800 },
  { width: 1920, height: 1200 },

  // 3:2
  { width: 720, height: 480 },
  { width: 1152, height: 768 },
  { width: 1800, height: 1200 },

  // 5:4
  { width: 1280, height: 1024 },
  // { width: 2560, height: 2048 },
]

export const SettingsArea: React.FC<{
  width?: string
  availableCameras: CameraDevice[]
}> = ({
  width = '320px',
  availableCameras,
}) => {
  const { settings, updateSettings } = useSettings()
  const [isCollapsed, setIsCollapsed] = useState<boolean>(true)

  const collapseSettings = useCallback(() => {
    setIsCollapsed(!isCollapsed)
  }, [isCollapsed])

  const makeCheckbox = useCallback((key: string, text: string) => {
    return (
      <div style={{ cursor: 'pointer' }}>
        <label style={{ cursor: 'pointer' }}>
          {text}
          <input
            type="checkbox"
            checked={settings[key] ?? false}
            onChange={() => updateSettings({ [key]: !settings[key] })}
            style={{
              position: 'absolute',
              right: '8px',
              cursor: 'pointer',
            }}
          />
        </label>
      </div>
    )
  }, [settings, updateSettings])

  return (
    <div style={{
      backgroundColor: '#000000d0',
      position: 'absolute',
      zIndex: 100000,
      height: '100vh',
      width,
      top: 0,
      left: isCollapsed ? `-${width}` : '0px',
      padding: '0px',
      borderRight: isCollapsed ? 'none' : '1px solid #ffffffd0',
      transition: 'left 0.3s ease-in-out',
    }}>
      <div style={{
        padding: '8px',
        wordBreak: 'break-word',
        textAlign: 'left',
      }}>
        <button
          onClick={collapseSettings}
          style={{
            color: 'white',
            backgroundColor: '#000000d0',
            position: 'absolute',
            right: '-20px',
            width: '20px',
            height: '28px',
            padding: '0px',
            border: '1px solid #ffffffd0',
            borderLeft: 'none',
          }}
        >
          {isCollapsed ? '>' : '<'}
        </button>

        <div style={{ color: 'white' }}>
          {/* Display Settings */}
          <div style={{ marginBottom: '20px' }}>
            <h3>Display Settings</h3>
            <div style={{ marginLeft: '20px' }}>
              {availableCameras.length > 0 ? (
                <>
                  <span>Camera: </span>
                  <select
                    value={settings.selectedCameraId}
                    onChange={(e) => updateSettings({ selectedCameraId: e.target.value, selectedCameraLabel: e.target.selectedOptions[0].text })}
                    style={{
                      border: '1px solid #555',
                      borderRadius: '8px',
                      width: '120px',
                      cursor: 'pointer',
                      textAlign: 'center',
                      position: 'absolute',
                      right: '8px',
                    }}
                  >
                    {availableCameras.map((camera) => (
                      <option key={camera.deviceId} value={camera.deviceId}>
                        📷 {camera.label}
                      </option>
                    ))}
                  </select>
                </>
              ) : (
                <div style={{ fontSize: '12px', color: 'red' }}>No cameras found</div>
              )
              }

              <br />

              <span>Camera Resolution: </span>
              <select
                value={settings.preferredResolution?.width + 'x' + settings.preferredResolution?.height}
                onChange={(e) => updateSettings({ preferredResolution: { width: parseInt(e.target.value.split('x')[0]), height: parseInt(e.target.value.split('x')[1]) } })}
                style={{
                  border: '1px solid #555',
                  borderRadius: '8px',
                  width: '120px',
                  cursor: 'pointer',
                  textAlign: 'center',
                  position: 'absolute',
                  right: '8px',
                }}>
                {resolutions.map((resolution) => {
                  const d = gcd(resolution.width, resolution.height)

                  return <option key={resolution.width + 'x' + resolution.height} value={resolution.width + 'x' + resolution.height}>{resolution.width}x{resolution.height} ({resolution.width / d}:{resolution.height / d})</option>
                })}
              </select>

              {makeCheckbox('display_showVideo', 'Show Video Feed')}
              {makeCheckbox('display_showStickFigure', 'Show Tracking Points')}
              {makeCheckbox('display_show3DAvatar', 'Show 3D Avatar')}
              {makeCheckbox('enableEnhancedHands', 'Enable Enhanced Hands')}
            </div>
          </div>
          {/* Preview Settings */}
          <div style={{ marginBottom: '20px' }}>
            <h3>Preview Settings</h3>
            <div style={{ marginLeft: '20px' }}>
              {makeCheckbox('preview_showVideo', 'Show Exercise Preview')}
              {makeCheckbox('preview_showStickFigure', 'Show Stick Figure')}
              {makeCheckbox('debug_showGraph', 'Show Graph')}
              {makeCheckbox('debug_showStatus', 'Show Status')}
            </div>
          </div>

          {/* Debug Settings */}
          <div style={{ marginBottom: '20px' }}>
            <h3>Debug Settings</h3>
            <div style={{ marginLeft: '20px' }}>
              {makeCheckbox('debug_showJointBars', 'Show Joint Bars')}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
